import { useCallback, useEffect, useRef, useState } from 'react';
import { type Workspace } from '@oysterjs/smartfill-contracts';
import { useLocation } from 'react-router-dom';
import { CaraMark } from '../components/CaraMark';
import { BucketRows } from '../components/BucketRows';
import { WorkspaceSearch, type WorkspaceSearchValue } from '../components/WorkspaceSearch';
import { ChevronRightIcon, SnapshotIcon } from '../components/Icons';
import { RemoteListStates } from '../components/RemoteListStates';
import { InlineError } from '../components/SidebarState';
import { useActivateBucket, useBucketList, useWorkspaceList } from '../sidebar/data';
import {
  useWorkspaceConversion,
  type WorkspaceConversionState
} from '../sidebar/workspaceConversion';
import { useSidebarHistory } from '../sidebar/history';
import { bucketPath } from '../sidebar/routes';
import { formatRelativeTime } from '../sidebar/presentation';

/** Lists workspaces for a model to read whole and convert into general buckets. */
export function WorkspacesView() {
  const [{ query: search, selection }, setSearch] = useState<WorkspaceSearchValue>({
    query: '',
    selection: 'me'
  });
  const [completed, setCompleted] = useState<Record<string, number>>({});
  const workspaces = useWorkspaceList(search, selection);
  const conversion = useWorkspaceConversion();
  const activate = useActivateBucket();
  const history = useSidebarHistory();
  const location = useLocation();
  const launchedWorkspace = useRef<string | null>(null);
  const resumedWorkspaces = useRef(new Set<string>());
  const generate = useCallback(
    async (workspaceId: string) => {
      const bucketIds = await conversion.convert(workspaceId);
      if (!bucketIds) return;
      setCompleted((previous) => ({
        ...previous,
        [workspaceId]: (previous[workspaceId] ?? 0) + 1
      }));
      if (bucketIds.length === 1) {
        await activate.activate(bucketIds[0]);
        history.go(bucketPath(bucketIds[0]));
        return;
      }
    },
    [activate.activate, conversion.convert, history]
  );

  useEffect(() => {
    for (const workspaceId of conversion.activeWorkspaceIds) {
      if (resumedWorkspaces.current.has(workspaceId)) continue;
      resumedWorkspaces.current.add(workspaceId);
      void generate(workspaceId);
    }
  }, [conversion.activeWorkspaceIds, generate]);

  useEffect(() => {
    const workspaceId = new URLSearchParams(location.search).get('generate');
    if (!workspaceId || launchedWorkspace.current === workspaceId) return;
    launchedWorkspace.current = workspaceId;
    void generate(workspaceId);
  }, [generate, location.search]);

  return (
    <section className="workspaces-view" aria-labelledby="workspaces-heading">
      <WorkspaceSearch onChange={setSearch} />

      <div className="section-heading">
        <h2 id="workspaces-heading">
          {search.trim() ? 'Matching workspaces' : 'Recent workspaces'}
        </h2>
      </div>

      {activate.error && <InlineError message={activate.error.message} />}
      <RemoteListStates
        list={workspaces}
        searching={Boolean(search.trim())}
        loading={{
          title: 'Loading workspaces',
          description: 'Finding recent Cara workspace runs.'
        }}
        errorTitle="Workspaces could not load"
        empty={{
          searchTitle: 'No matching workspaces',
          searchDescription: 'Try another workspace or client name.',
          initialTitle: 'No recent workspaces',
          initialDescription: 'Try another user, or run a Cara workspace to create data buckets.'
        }}
      />

      <div className="workspace-list">
        {workspaces.items.map((workspace) => (
          <WorkspaceCard
            key={workspace.id}
            workspace={workspace}
            conversion={conversion.stateFor(workspace.id)}
            completedRevision={completed[workspace.id] ?? 0}
            onGenerate={() => void generate(workspace.id)}
          />
        ))}
      </div>
    </section>
  );
}

function WorkspaceCard({
  workspace,
  conversion,
  completedRevision,
  onGenerate
}: {
  workspace: Workspace;
  conversion: WorkspaceConversionState;
  completedRevision: number;
  onGenerate: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    if (completedRevision) setExpanded(true);
  }, [completedRevision]);
  const isGenerating = conversion.status === 'loading';
  const progressDescriptionId = `workspace-generation-${workspace.id}`;
  const label = generateLabel(conversion.status);

  return (
    <article className="workspace-card">
      <div className="workspace-card__row">
        <span className="workspace-card__icon" aria-hidden="true">
          <CaraMark />
        </span>
        <span className="workspace-card__copy">
          <strong>{workspace.title}</strong>
          <span>
            {workspace.clientName ? `Copilot · ${workspace.clientName}` : 'Copilot workspace'}
          </span>
        </span>
        <time dateTime={workspace.updatedAt}>{formatRelativeTime(workspace.updatedAt)}</time>
      </div>
      <div className="section-heading section-heading--workspace-buckets">
        <h3>
          <button
            type="button"
            className="entity-toggle"
            aria-expanded={expanded}
            aria-label={`Data buckets from ${workspace.title}`}
            aria-controls={`workspace-buckets-${workspace.id}`}
            onClick={() => setExpanded((value) => !value)}
          >
            <ChevronRightIcon className="entity-toggle__chevron" /> Data buckets
          </button>
        </h3>
      </div>
      {expanded && (
        <WorkspaceBuckets key={`${workspace.id}:${completedRevision}`} workspace={workspace} />
      )}
      {conversion.error && <InlineError message={conversion.error.message} />}
      <button
        type="button"
        className={`workspace-generate${isGenerating ? ' workspace-generate--loading' : ''}`}
        disabled={isGenerating}
        aria-busy={isGenerating}
        aria-describedby={isGenerating ? progressDescriptionId : undefined}
        onClick={onGenerate}
      >
        <span className="workspace-generate__content">
          <SnapshotIcon />
          {label}
        </span>
        {isGenerating && <span className="workspace-generate__progress" aria-hidden="true" />}
      </button>
      {isGenerating && (
        <p
          id={progressDescriptionId}
          className="workspace-generate__message"
          role="status"
          aria-live="polite"
        >
          This can take a few minutes. You can close this panel; Cara will keep working.
        </p>
      )}
    </article>
  );
}

function generateLabel(status: WorkspaceConversionState['status']): string {
  if (status === 'loading') return 'Generating data buckets…';
  return status === 'error' ? 'Retry generation' : 'Generate data buckets';
}

function WorkspaceBuckets({ workspace }: { workspace: Workspace }) {
  const buckets = useBucketList('', workspace.id);
  return (
    <div
      className="workspace-buckets"
      id={`workspace-buckets-${workspace.id}`}
      role="region"
      aria-label={`Data buckets from ${workspace.title}`}
    >
      <RemoteListStates
        list={buckets}
        searching={false}
        loading={{
          title: 'Loading linked buckets',
          description: 'Finding data created from this workspace.'
        }}
        errorTitle="Linked buckets could not load"
        empty={{
          searchTitle: 'No linked data buckets',
          searchDescription: '',
          initialTitle: 'No linked data buckets',
          initialDescription:
            'Generate data buckets below. Older buckets without a saved workspace link are still in Data buckets.'
        }}
      />
      <BucketRows items={buckets.items} onChanged={buckets.reload} />
      {buckets.items.length === 100 && (
        <p className="workspace-buckets__hint">
          Showing the 100 most recently updated linked buckets. Search Data buckets for older
          results.
        </p>
      )}
    </div>
  );
}
